import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO, { locationSchema, orgSchema, faqSchema, breadcrumbSchema, videoSchema } from "../../lib/seo";
import { LOCATIONS, MEDIA, trackConversion, trackPhoneClick } from "../../data/site";
import { getAttribution, newSubmissionId } from "../../lib/attribution";
import { SUBURBS } from "../../data/suburbs";
import { programBySlug } from "../../data/programs";
import { GLOBAL_FAQS } from "../../data/content";
import { Reveal, Eyebrow, TrialButton, SectionShell, FAQBlock, VideoPlayer } from "../../components/shared";
import { ScheduleFilterable, LocationCards } from "../../components/interactive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact free-trial form — same pipeline, dedupe and exactly-once free_trial_lead as /hiddengym free trial.
const LocationTrialForm = ({ locationSlug, locationShort }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", email: "", goal: "", website: "", sms_consent: false, email_consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const subIdRef = useRef(newSubmissionId());
  const startedRef = useRef(false);
  const campaign = params.get("utm_campaign") || params.get("hgref") || `location-${locationSlug}`;
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (!startedRef.current) { startedRef.current = true; trackConversion("form_start", { form: "free_trial", page: `location-${locationSlug}` }); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads/private-lesson`, {
        ...form,
        location: locationSlug,
        kind: "free-trial",
        goal: `FREE TRIAL REQUEST: ${form.goal || "general trial"}`,
        submission_id: subIdRef.current,
        attribution: getAttribution(),
        utm_source: params.get("utm_source") || (params.get("hgref") ? "guymezger.com" : "direct"),
        utm_campaign: campaign,
      });
      trackConversion("free_trial_lead", { location: locationSlug, transaction_id: subIdRef.current, utm_campaign: campaign });
      trackConversion("generate_lead", { lead_type: "free_trial", location: locationSlug, transaction_id: subIdRef.current, utm_campaign: campaign });
      setDone(true);
      toast.success("Trial booked — see you on the mats!");
    } catch (err) {
      trackConversion("form_error", { form: "free_trial", code: err.response?.status || "network" });
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — call us and we'll book you by phone.");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full px-4 py-3 border border-black/20 bg-white text-[#1E2229] focus:outline-none focus:border-[#0A0B0D]";
  if (done) {
    return (
      <div className="bg-[#131518] text-white p-8" data-testid="nd-trial-success">
        <h3 className="font-display font-black uppercase text-2xl">You're booked.</h3>
        <p className="mt-2 text-white/70">Hidden Gym staff will confirm your class time by phone or email. Wear athletic clothes, bring water, and arrive 15 minutes early to sign the waiver.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="grid gap-3 border border-black/10 bg-white p-6" data-testid="nd-trial-form" aria-label={`Book a free trial class at ${locationShort}`}>
      <div>
        <label htmlFor="nd-name" className="block text-sm font-bold mb-1">Name</label>
        <input id="nd-name" required autoComplete="name" value={form.name} onChange={set("name")} className={input} data-testid="nd-trial-name-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="nd-phone" className="block text-sm font-bold mb-1">Phone</label>
          <input id="nd-phone" type="tel" required autoComplete="tel" value={form.phone} onChange={set("phone")} className={input} data-testid="nd-trial-phone-input" />
        </div>
        <div>
          <label htmlFor="nd-email" className="block text-sm font-bold mb-1">Email</label>
          <input id="nd-email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} className={input} data-testid="nd-trial-email-input" />
        </div>
      </div>
      <div>
        <label htmlFor="nd-goal" className="block text-sm font-bold mb-1">What interests you? (optional)</label>
        <input id="nd-goal" value={form.goal} onChange={set("goal")} placeholder="e.g. Muay Thai, BJJ, kids class…" className={input} data-testid="nd-trial-goal-input" />
      </div>
      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="nd-trial-honeypot" />
      <div className="space-y-2 text-sm text-[#374151]">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={form.sms_consent} onChange={set("sms_consent")} className="mt-1" data-testid="nd-trial-sms-consent" />
          <span>OK to text me about my trial and occasional class updates (SMS)</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={form.email_consent} onChange={set("email_consent")} className="mt-1" data-testid="nd-trial-email-consent" />
          <span>OK to email me schedule news and offers</span>
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="nd-trial-submit-btn"
        className="bg-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide px-6 py-3.5 hover:bg-[#1E2229] transition-colors disabled:opacity-60">
        {busy ? "Booking…" : `Book My Free Trial at ${locationShort}`}
      </button>
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">First class free · no experience needed · no sparring on day one</p>
    </form>
  );
};

/* Verified location media — see /app/docs/media-manifest.md */
const LOC_MEDIA = {
  richardson: {
    hero: { src: MEDIA.photos.richardsonMats, alt: "The mats and cage room at Mezger Martial Arts, Hidden Gym Richardson" },
    tour: {
      ...MEDIA.videos.richardsonTour,
      portrait: true,
      title: "Hidden Gym Richardson facility tour",
      caption: "Facility tour · Hidden Gym Richardson (~1:30)",
      schemaName: "Hidden Gym Richardson Facility Tour",
      schemaDesc: "Walkthrough tour of the Hidden Gym Richardson facility at 235 N Central Expy — martial arts room, strength floor, turf, and amenities.",
    },
    gallery: [
      { src: MEDIA.photos.richardson1, alt: "Exterior of Hidden Gym Richardson at 235 N Central Expy", caption: "The building · 235 N Central Expy" },
      { src: MEDIA.photos.richardsonWeights, alt: "Strength training floor at Hidden Gym Richardson", caption: "Strength floor · shared gym amenity" },
      { src: MEDIA.photos.richardsonTurf, alt: "Turf training area at Hidden Gym Richardson", caption: "Turf · shared gym amenity" },
      { src: MEDIA.photos.bjjRichardson, alt: "Martial arts class on the mats at Hidden Gym Richardson", caption: "Martial arts class · the Mezger room" },
      { src: MEDIA.photos.richardsonLockers, alt: "Locker rooms at Hidden Gym Richardson", caption: "Locker rooms" },
    ],
  },
  "north-dallas": {
    hero: { src: MEDIA.photos.northDallasMats, alt: "The 2026 martial arts mat room at Mezger Martial Arts, Hidden Gym North Dallas" },
    tour: {
      ...MEDIA.videos.northDallasMmaTour,
      portrait: false,
      title: "Mezger Martial Arts room tour at Hidden Gym North Dallas",
      caption: "Martial arts room tour · North Dallas (2026 layout)",
      schemaName: "Mezger Martial Arts Room Tour — Hidden Gym North Dallas",
      schemaDesc: "Tour of the 2026 Mezger Martial Arts mat room at Hidden Gym North Dallas, 17390 Preston Rd — mats, heavy bags, and wall pads.",
    },
    fullTour: {
      ...MEDIA.videos.northDallasFullTour,
      title: "Full Hidden Gym North Dallas facility tour",
      caption: "See the full gym · Hidden Gym North Dallas (~45s)",
    },
    gallery: [
      { src: MEDIA.photos.northDallasBags, alt: "Mezger Martial Arts heavy bags at Hidden Gym North Dallas", caption: "Heavy bags · the Mezger room" },
      { src: MEDIA.photos.northDallasWallPads, alt: "Wall pads in the martial arts room at Hidden Gym North Dallas", caption: "Wall pads · the Mezger room" },
      { src: MEDIA.photos.northDallas1, alt: "Training floor and equipment at Hidden Gym North Dallas", caption: "Training floor" },
      { src: MEDIA.photos.northDallasWeights, alt: "Strength training floor at Hidden Gym North Dallas", caption: "Strength floor · shared gym amenity" },
      { src: MEDIA.photos.northDallasCardio, alt: "Cardio equipment and turf at Hidden Gym North Dallas", caption: "Cardio & turf · shared gym amenity" },
    ],
  },
};

export const LocationsIndex = () => (
  <>
    <SEO
      title="Locations | Mezger Martial Arts at Hidden Gym — Richardson & North Dallas"
      description="Train at Mezger Martial Arts inside Hidden Gym: 235 N Central Expy, Richardson TX, and our North Dallas facility. Maps, hours, programs, and free trial booking."
      schemas={[orgSchema(), ...LOCATIONS.map(locationSchema), breadcrumbSchema([["Home", "/"], ["Locations", "/locations"]])]}
    />
    <section className="bg-[#0A0B0D] text-white pt-24 pb-16 grain relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Locations</Eyebrow>
          <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">Two Gyms.<br /><span className="text-neutral-400">One Standard.</span></h1>
          <p className="mt-4 max-w-2xl text-white/70">Both locations run Guy's curriculum with his coaching staff. Your Hidden Gym membership works at both.</p>
        </Reveal>
      </div>
    </section>
    <SectionShell><LocationCards withMaps /></SectionShell>
    <SectionShell className="bg-white">
      <Reveal>
        <Eyebrow>Coming From Nearby?</Eyebrow>
        <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl">We Serve the Whole Corridor</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {SUBURBS.map((s) => (
            <Link key={s.slug} to={`/near/${s.slug}`} data-testid={`locations-near-link-${s.slug}`}
              className="border border-black/15 px-4 py-2 text-sm font-bold uppercase tracking-wide hover:border-[#0A0B0D] hover:text-[#0A0B0D] transition-colors">
              Martial arts near {s.city}
            </Link>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  </>
);

export default function LocationPage() {
  const { slug } = useParams();
  const loc = LOCATIONS.find((l) => l.slug === slug);
  if (!loc) return <Navigate to="/locations" replace />;
  const locFaqs = GLOBAL_FAQS.filter((f) => /location|both/i.test(f.q));
  const media = LOC_MEDIA[slug];
  return (
    <>
      <SEO
        title={`${loc.name} | Martial Arts in ${loc.short} TX`}
        description={`Muay Thai, Boxing, BJJ${loc.slug === "richardson" ? ", Wrestling" : ""} & Kids martial arts at ${loc.name}. ${loc.address} · ${loc.phone}. Book a free trial class.`}
        schemas={[locationSchema(loc), faqSchema(locFaqs), videoSchema({ name: media.tour.schemaName, description: media.tour.schemaDesc, path: media.tour.src, poster: media.tour.poster, uploadDate: "2026-09-15", duration: media.tour.duration }), breadcrumbSchema([["Home", "/"], ["Locations", "/locations"], [loc.short, `/locations/${loc.slug}`]])]}
      />
      <section className="relative bg-[#0A0B0D] text-white pt-24 pb-16 overflow-hidden grain">
        <img src={media.hero.src} alt={media.hero.alt} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/60 to-[#0A0B0D]/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Mezger Martial Arts at Hidden Gym</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none"><span className="text-neutral-400">{loc.short}</span></h1>
            <p className="mt-4 text-white/70 max-w-2xl">{loc.address} · <a href={loc.phoneHref} onClick={() => trackPhoneClick(loc.slug, loc.phoneHref)} className="underline hover:text-white" data-testid={`location-hero-phone-${loc.slug}`}>{loc.phone}</a> · ★ {loc.rating.value.toFixed(1)} from {loc.rating.count} Google reviews</p>
            <div className="mt-8"><TrialButton campaign={`location-${loc.slug}`} page={`location-${loc.slug}`} location={loc.slug} testId="location-hero-trial-btn">Book a Free Trial at {loc.short}</TrialButton></div>
          </Reveal>
        </div>
      </section>

      {slug === "north-dallas" && (
        <SectionShell className="bg-[#F4F4F2]">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <Eyebrow>Free Beginner Trial</Eyebrow>
              <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">Your First Class<br /><span className="text-[#5A6270]">Is Free.</span></h2>
              <p className="mt-4 text-[#374151] leading-relaxed max-w-lg">Most people who walk in have never trained a day in their life — that's exactly who our coaches are best at. Gloves and pads provided, no experience needed, and no sparring on day one, ever.</p>
              <img src="/media/photos/coaching-pads.webp" srcSet="/media/photos/coaching-pads-800.webp 800w, /media/photos/coaching-pads.webp 1600w" sizes="(min-width: 1024px) 45vw, 100vw"
                width="1600" height="1067" loading="lazy" alt="Coach working hands-on with two boxing students during a class at Mezger Martial Arts"
                className="mt-6 w-full aspect-[16/9] object-cover border border-black/10" data-testid="nd-trial-coaching-photo" />
            </Reveal>
            <Reveal delay={0.1}>
              <LocationTrialForm locationSlug="north-dallas" locationShort={loc.short} />
            </Reveal>
          </div>
        </SectionShell>
      )}

      <SectionShell dark className="grain">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <Eyebrow>Take the Tour</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">Walk Through<br /><span className="text-neutral-400">{loc.short}.</span></h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
              {slug === "richardson"
                ? "A real walkthrough of our Richardson headquarters — the martial arts room where classes run, plus the full Hidden Gym floor your membership includes: strength equipment, turf, and locker rooms."
                : "A tour of the 2026 Mezger Martial Arts mat room at Hidden Gym North Dallas — the mats, heavy bags, and wall pads where every class runs. Your membership also includes the full Hidden Gym floor."}
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-widest text-white/40">Martial arts room + shared gym amenities are separate spaces in the same building.</p>
            {media.fullTour && (
              <div className="mt-8 max-w-md">
                <VideoPlayer src={media.fullTour.src} poster={media.fullTour.poster} title={media.fullTour.title} caption={media.fullTour.caption} dark testId="location-full-tour-video" />
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1} className={media.tour.portrait ? "flex lg:justify-end" : ""}>
            <VideoPlayer
              src={media.tour.src}
              poster={media.tour.poster}
              title={media.tour.title}
              caption={media.tour.caption}
              portrait={media.tour.portrait}
              dark
              testId="location-tour-video"
              className={media.tour.portrait ? "w-full max-w-sm" : "w-full"}
            />
          </Reveal>
        </div>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow>The Facility</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">Inside {loc.short}</h2>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`location-gallery-${slug}`}>
          {media.gallery.map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.08}>
              <figure>
                <img src={g.src} alt={g.alt} loading="lazy" className="w-full aspect-video object-cover border border-black/10" />
                <figcaption className="mt-2 font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">{g.caption}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </SectionShell>

      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <iframe title={`Map of ${loc.name}`} src={loc.mapEmbed} className="w-full h-full min-h-[360px] border border-black/10" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </Reveal>
          <Reveal delay={0.1} className="space-y-5">
            <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">Visit Us</h2>
            <dl className="space-y-3 text-[#374151]">
              <div><dt className="font-bold">Address</dt><dd className={loc.address.startsWith("[") ? "bg-amber-100 text-amber-900 inline-block px-2 py-0.5 font-mono text-sm" : ""}>{loc.address}</dd></div>
              <div><dt className="font-bold">Phone</dt><dd><a href={loc.phoneHref} onClick={() => trackPhoneClick(loc.slug, loc.phoneHref)} className="underline hover:text-neutral-500" data-testid={`location-visit-phone-${loc.slug}`}>{loc.phone}</a></dd></div>
              <div><dt className="font-bold">Hours</dt><dd className={loc.hours.startsWith("[") ? "bg-amber-100 text-amber-900 inline-block px-2 py-0.5 font-mono text-sm" : ""}>{loc.hours}</dd></div>
              <div><dt className="font-bold">Parking</dt><dd>{loc.parking}</dd></div>
            </dl>
            <a href={loc.mapLink} target="_blank" rel="noopener noreferrer" className="inline-block border border-[#0A0B0D] font-display font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#0A0B0D] hover:text-white transition-colors" data-testid="location-directions-btn">Get Directions</a>
            <div>
              <h3 className="font-display font-black uppercase text-xl mt-4">Programs at {loc.short}</h3>
              <ul className="mt-3 flex flex-wrap gap-2">
                {loc.programs.map((p) => {
                  const prog = programBySlug(p);
                  return <li key={p}><Link to={`/programs/${p}`} className="inline-block text-sm font-bold border border-black/15 px-3 py-1.5 hover:border-[#0A0B0D] hover:text-[#0A0B0D] transition-colors">{prog.name}</Link></li>;
                })}
              </ul>
            </div>
          </Reveal>
        </div>
      </SectionShell>
      <SectionShell>
        <Reveal>
          <Eyebrow>Schedule</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase text-2xl sm:text-3xl lg:text-4xl">This Week at {loc.short}</h2>
        </Reveal>
        <div className="mt-8"><ScheduleFilterable lockLocation={loc.slug} compact /></div>
      </SectionShell>
      <SectionShell className="bg-white">
        <Reveal><Eyebrow>Location FAQs</Eyebrow></Reveal>
        <div className="mt-6 max-w-3xl"><FAQBlock faqs={locFaqs} testPrefix={`faq-loc-${loc.slug}`} /></div>
        <p className="mt-6 text-sm text-[#5A6270]">Also see: <Link to={`/locations/${loc.slug === "richardson" ? "north-dallas" : "richardson"}`} className="underline hover:text-neutral-500">our {loc.slug === "richardson" ? "North Dallas" : "Richardson"} location</Link> · <Link to="/membership" className="underline hover:text-neutral-500">membership via Hidden Gym</Link></p>
      </SectionShell>
    </>
  );
}
